import React from 'react';
import { List, TextField, FunctionField, TextInput, useTranslate } from 'react-admin';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import {
  ListScreen, PageTitle, RenameRowActions, ResponsiveDatagrid, SectionHeader,
} from '@/components/shell';

interface DocumentTypeRecord {
  id: number;
  name: string;
  mappedFields?: string[];
}

/** The fields the OCR engine reads for this type, or a note that it uses the defaults. */
const MappedFields: React.FC<{ record: DocumentTypeRecord }> = ({ record }) => {
  const t = useTranslate();
  const fields = record?.mappedFields ?? [];
  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        {t('screens.documentTypes.usesDefault')}
      </Typography>
    );
  }
  const visible = fields.slice(0, 6);
  const rest = fields.length - visible.length;
  return (
    <Typography variant="body2" color="text.secondary" component="span">
      {visible.join(', ')}{rest > 0 ? ` ${t('screens.documentTypes.more', { count: rest })}` : ''}
    </Typography>
  );
};

/** Row actions: rename in place, or delete after confirming. */
const DocTypeActions: React.FC<{ record: DocumentTypeRecord }> = ({ record }) => {
  const t = useTranslate();
  return (
    <RenameRowActions
      name={record?.name || ''}
      nameLabel={t('resources.document-types.fields.name')}
      onRename={(name) => httpClient.put('/admin/document-type/UpdateDocumentType', { id: record.id, name })}
      onDelete={() => httpClient.delete(`/admin/document-type/DeleteDocumentType/${record.id}`)}
    />
  );
};

export const DocumentTypeList = () => {
  const t = useTranslate();
  const navigate = useNavigate();

  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
  ];

  return (
    <ListScreen
      header={
        <SectionHeader
          left={
            <PageTitle
              title={t('resources.document-types.name', { smart_count: 2 })}
              subtitle={t('screens.documentTypes.subtitle')}
            />
          }
          create={{ label: t('common.create'), onClick: () => navigate('/document-types/create') }}
        />
      }
    >
      <List
        filters={filters}
        actions={false}
        empty={false}
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
      >
        <ResponsiveDatagrid<DocumentTypeRecord>
          rowClick={false}
          mobile={{
            primary: (r) => r.name,
            lines: [{ label: t('resources.document-types.fields.mappingsCount'), render: (r) => <MappedFields record={r} /> }],
            actions: (r) => <DocTypeActions record={r} />,
          }}
        >
          <TextField source="name" label={t('resources.document-types.fields.name')} sortBy="name" />
          <FunctionField
            label={t('resources.document-types.fields.mappingsCount')}
            sortable={false}
            render={(r: DocumentTypeRecord) => <MappedFields record={r} />}
          />
          <FunctionField
            label=""
            textAlign="right"
            sortable={false}
            render={(r: DocumentTypeRecord) => <DocTypeActions record={r} />}
          />
        </ResponsiveDatagrid>
      </List>
    </ListScreen>
  );
};
